import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, User } from "lucide-react";
import { MdShoppingCart } from "react-icons/md";

export default function Navbar() {
  const [isOpen, setIsOpen] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const navigate = useNavigate();
  const dropdownRef = useRef(null);

  const toggleMenu = () => setIsOpen(!isOpen);

  // Calculate total quantity
  const getCartCount = (cart) =>
    cart.reduce((sum, item) => sum + (item.quantity || 1), 0);

  useEffect(() => {
    const cart = JSON.parse(localStorage.getItem("cart")) || [];
    setCartCount(getCartCount(cart));
  }, []);

  useEffect(() => {
    const handleStorageChange = () => {
      const cart = JSON.parse(localStorage.getItem("cart")) || [];
      setCartCount(getCartCount(cart));
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <nav className="bg-pink-600 shadow-md fixed w-full z-50 h-24 flex items-center">
      <div className="w-full px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-full">
          {/* Logo */}
          <Link to="/" className="flex items-center mt-[-60px]">
            <img
              src="https://ik.imagekit.io/gxibnx8tv/WhatsApp_Image_2025-09-25_at_21.57.10-removebg-preview.png?updatedAt=1758817772219"
              alt="Accessories Girly Logo"
              className="h-[220px]  w-auto object-contain"
            />
          </Link>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            <Link to="/" className="text-white hover:text-pink-200">Home</Link>
            <Link to="/products" className="text-white hover:text-pink-200">Products</Link>
            <Link to="/categories" className="text-white hover:text-pink-200">Category</Link>
            <Link to="/about" className="text-white hover:text-pink-200">About Us</Link>
            <Link to="/contact" className="text-white hover:text-pink-200">Contact Us</Link>

            {/* Cart Icon */}
            <div className="relative cursor-pointer" onClick={() => navigate('/cart')}>
              <MdShoppingCart size={24} className="text-white" />
              {cartCount > 0 && (
                <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                  {cartCount}
                </span>
              )}
            </div>

            {/* Profile Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button onClick={() => setDropdownOpen(!dropdownOpen)} className="flex items-center">
                <User size={28} className="text-white" />
              </button>
              {dropdownOpen && (
                <div className="absolute right-0 mt-2 w-40 bg-white rounded-lg shadow-lg text-gray-800 py-2 z-50">
                  <Link
                    to="/profile"
                    className="block px-4 py-2 hover:bg-pink-50"
                    onClick={() => setDropdownOpen(false)}
                  >
                    Profile
                  </Link>
                  <Link
                    to="/my-orders"
                    className="block px-4 py-2 hover:bg-pink-50"
                    onClick={() => setDropdownOpen(false)}
                  >
                    My Orders
                  </Link>
                  <button
                    onClick={() => {
                      localStorage.removeItem("token");
                      localStorage.removeItem('userId');
                      setDropdownOpen(false);
                      navigate("/login");
                    }}
                    className="w-full text-left px-4 py-2 hover:bg-pink-50"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Mobile Menu Button */}
          <div className="md:hidden flex items-center">
            <button onClick={toggleMenu} className="text-white">
              {isOpen ? <X size={28} /> : <Menu size={28} />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Sidebar/Menu */}
      {isOpen && (
        <div className="absolute top-24 left-0 w-full bg-pink-600 text-white px-4 pt-4 pb-6 space-y-4 md:hidden">
          <Link to="/" className="block hover:text-pink-200" onClick={toggleMenu}>Home</Link>
          <Link to="/products" className="block hover:text-pink-200" onClick={toggleMenu}>Products</Link>
          <Link to="/categories" className="block hover:text-pink-200" onClick={toggleMenu}>Category</Link>
          <Link to="/about" className="block hover:text-pink-200" onClick={toggleMenu}>About Us</Link>
          <Link to="/contact" className="block hover:text-pink-200" onClick={toggleMenu}>Contact Us</Link>

          {/* Cart Icon in Mobile Menu */}
          <div className="relative mt-4 flex items-center" onClick={() => navigate('/cart')}>
            <MdShoppingCart size={28} />
            {cartCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-green-500 text-white text-xs w-5 h-5 flex items-center justify-center rounded-full">
                {cartCount}
              </span>
            )}
            <span className="ml-2">Cart</span>
          </div>

          {/* Profile Options in Mobile */}
          <div className="mt-4 border-t border-pink-400 pt-2 space-y-2">
            <Link to="/profile" className="block hover:text-pink-200" onClick={toggleMenu}>Profile</Link>
            <Link to="/my-orders" className="block hover:text-pink-200" onClick={toggleMenu}>My Orders</Link>
            <button
              onClick={() => {
                localStorage.removeItem("token");
                localStorage.removeItem('userId');
                toggleMenu();
                navigate("/login");
              }}
              className="w-full text-left hover:text-pink-200"
            >
              Logout
            </button>
          </div>
        </div>
      )}
    </nav>
  );
}
